/**
 * Telegram WebApp adapter. Lazily reads `window.Telegram.WebApp`.
 * Outside Telegram we expose a no-op fallback so dev works in a plain browser.
 */

interface TWebApp {
  initData?: string;
  initDataUnsafe?: { user?: { id: number; first_name: string; username?: string; language_code?: string } };
  themeParams?: Record<string, string>;
  colorScheme?: 'light' | 'dark';
  /** Numeric WebApp version, e.g. "7.10". Always set on Telegram clients. */
  version?: string;
  platform?: string;
  /** Returns true if WebApp version is >= the passed version. Telegram >=6.0 has it. */
  isVersionAtLeast?: (v: string) => boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  MainButton: {
    setText: (s: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    /** Bot API 6.1+. Forces Waylo brand colors instead of the user's
     *  Telegram theme button color (which is often blue). */
    setParams?: (p: {
      color?: string;
      text_color?: string;
      text?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (t: 'success' | 'error' | 'warning') => void;
    selectionChanged: () => void;
  };
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
  showPopup?: (params: { title?: string; message: string; buttons?: { id?: string; type?: string; text?: string }[] }) => void;
  /** Bot API 6.2+. Returns true if user confirmed. */
  showConfirm?: (message: string, cb?: (ok: boolean) => void) => void;
  /** Bot API 6.2+. Single-button alert. */
  showAlert?: (message: string, cb?: () => void) => void;
  onEvent?: (e: string, cb: () => void) => void;
  offEvent?: (e: string, cb: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TWebApp };
  }
}

export function getWebApp(): TWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInsideTelegram(): boolean {
  return Boolean(window.Telegram?.WebApp && window.Telegram.WebApp.initData);
}

export function applyTelegramTheme(): void {
  const tg = getWebApp();
  if (!tg?.themeParams) return;
  const root = document.documentElement;
  const map: Record<string, string> = {
    bg_color: '--tg-theme-bg-color',
    secondary_bg_color: '--tg-theme-secondary-bg-color',
    text_color: '--tg-theme-text-color',
    hint_color: '--tg-theme-hint-color',
    link_color: '--tg-theme-link-color',
    button_color: '--tg-theme-button-color',
    button_text_color: '--tg-theme-button-text-color',
  };
  for (const [k, cssVar] of Object.entries(map)) {
    const v = tg.themeParams[k];
    if (v) root.style.setProperty(cssVar, v);
  }
}

export function initTelegram(): void {
  const tg = getWebApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
  } catch {
    /* ignore */
  }
  applyTelegramTheme();
  if (tg.onEvent) {
    tg.onEvent('themeChanged', applyTelegramTheme);
  }
}

/**
 * HapticFeedback is only available on WebApp 6.1+. Older clients (notably
 * Telegram 6.0 which is still common on Android) expose the method but log a
 * console warning when called. We gate every haptic call behind isVersionAtLeast.
 */
function hapticsAvailable(): TWebApp['HapticFeedback'] | null {
  const tg = getWebApp();
  if (!tg?.HapticFeedback) return null;
  if (tg.isVersionAtLeast && !tg.isVersionAtLeast('6.1')) return null;
  return tg.HapticFeedback;
}

export function hapticTap(): void {
  hapticsAvailable()?.impactOccurred('light');
}

export function hapticPress(): void {
  hapticsAvailable()?.impactOccurred('medium');
}

export function hapticRigid(): void {
  hapticsAvailable()?.impactOccurred('rigid');
}

export function hapticSelection(): void {
  hapticsAvailable()?.selectionChanged();
}

export function hapticSuccess(): void {
  hapticsAvailable()?.notificationOccurred('success');
}

export function hapticWarning(): void {
  hapticsAvailable()?.notificationOccurred('warning');
}

export function hapticError(): void {
  hapticsAvailable()?.notificationOccurred('error');
}

export function hapticSoft(): void {
  // Bot API 6.1+; iOS distinguishes from light, Android coarsens.
  hapticsAvailable()?.impactOccurred(getWebApp()?.platform === 'android' ? 'medium' : 'soft');
}

/**
 * Telegram-native confirm dialog. On iOS/Android Telegram (6.2+) uses the
 * platform-native modal; outside Telegram falls back to window.confirm.
 * Returns a Promise<boolean>.
 */
export function showConfirm(message: string): Promise<boolean> {
  const tg = getWebApp();
  if (tg?.showConfirm && (!tg.isVersionAtLeast || tg.isVersionAtLeast('6.2'))) {
    return new Promise((resolve) => tg.showConfirm!(message, (ok) => resolve(Boolean(ok))));
  }
  return Promise.resolve(window.confirm(message));
}

/**
 * Telegram-native alert dialog. Falls back to window.alert outside Telegram.
 */
export function showAlert(message: string): Promise<void> {
  const tg = getWebApp();
  if (tg?.showAlert && (!tg.isVersionAtLeast || tg.isVersionAtLeast('6.2'))) {
    return new Promise((resolve) => tg.showAlert!(message, () => resolve()));
  }
  window.alert(message);
  return Promise.resolve();
}
